// src/data/blogPosts.js
export const blogPosts = [
    {
      id: 1,
      slug: "microservices-spring-boot",
      title: {
        fr: "Architecture Microservices avec Spring Boot",
        en: "Microservices Architecture with Spring Boot"
      },
      excerpt: {
        fr: "Découvrez comment concevoir une architecture microservices robuste avec Spring Boot et Spring Cloud.",
        en: "Learn how to design a robust microservices architecture with Spring Boot and Spring Cloud."
      },
      content: {
        fr: `
  # Architecture Microservices avec Spring Boot
  
  L'architecture microservices est devenue une approche incontournable pour concevoir des applications évolutives et maintenables. Dans cet article, nous allons explorer comment mettre en place une architecture microservices solide avec Spring Boot.
  
  ## Pourquoi les microservices ?
  
  Les microservices offrent plusieurs avantages par rapport aux applications monolithiques traditionnelles :
  
  - **Déploiement indépendant** : Chaque service peut être déployé séparément, ce qui facilite les mises à jour continues.
  - **Scalabilité précise** : Vous pouvez mettre à l'échelle uniquement les services qui nécessitent plus de ressources.
  - **Résilience** : La défaillance d'un service n'entraîne pas l'arrêt de l'ensemble de l'application.
  - **Technologie adaptée** : Chaque service peut utiliser la technologie la plus appropriée à sa fonction.
  
  ## Les composants essentiels d'une architecture microservices avec Spring
  
  ### 1. Spring Boot pour les services
  
  Spring Boot simplifie considérablement le développement de microservices grâce à sa configuration automatique et ses starters. Voici un exemple de service minimal :
  
  \`\`\`java
  @SpringBootApplication
  @RestController
  public class ProductServiceApplication {
  
      public static void main(String[] args) {
          SpringApplication.run(ProductServiceApplication.class, args);
      }
  
      @GetMapping("/products")
      public List<Product> getProducts() {
          // Logique pour récupérer les produits
          return productRepository.findAll();
      }
  }
  \`\`\`
  
  ### 2. Spring Cloud Gateway pour le routage API
  
  Spring Cloud Gateway sert de point d'entrée unifié pour votre architecture microservices :
  
  \`\`\`java
  @SpringBootApplication
  public class GatewayApplication {
  
      public static void main(String[] args) {
          SpringApplication.run(GatewayApplication.class, args);
      }
  
      @Bean
      public RouteLocator customRouteLocator(RouteLocatorBuilder builder) {
          return builder.routes()
              .route("product_service", r -> r.path("/products/**")
                  .uri("lb://PRODUCT-SERVICE"))
              .route("order_service", r -> r.path("/orders/**")
                  .uri("lb://ORDER-SERVICE"))
              .build();
      }
  }
  \`\`\`
  
  ### 3. Service Discovery avec Eureka
  
  Pour permettre aux services de se découvrir mutuellement :
  
  \`\`\`java
  @SpringBootApplication
  @EnableEurekaServer
  public class DiscoveryServerApplication {
      public static void main(String[] args) {
          SpringApplication.run(DiscoveryServerApplication.class, args);
      }
  }
  \`\`\`
  
  ### 4. Configuration centralisée avec Spring Cloud Config
  
  Pour gérer la configuration de tous vos services à un endroit central :
  
  \`\`\`java
  @SpringBootApplication
  @EnableConfigServer
  public class ConfigServerApplication {
      public static void main(String[] args) {
          SpringApplication.run(ConfigServerApplication.class, args);
      }
  }
  \`\`\`
  
  ## Communication entre services
  
  La communication entre microservices peut se faire via :
  
  1. **REST** avec RestTemplate ou WebClient
  2. **Messagerie asynchrone** avec Kafka ou RabbitMQ
  3. **gRPC** pour une communication plus efficace
  
  ## Sécurisation des microservices
  
  La sécurité est primordiale dans une architecture distribuée. Utilisez Spring Security avec OAuth2/JWT pour sécuriser les communications entre services.
  
  ## Bonnes pratiques
  
  1. **Base de données dédiée par service** : Chaque microservice devrait avoir sa propre base de données
  2. **Circuit breaker** : Utilisez Resilience4j pour gérer les défaillances des services
  3. **Monitoring** : Mettez en place une solution de monitoring comme Spring Boot Admin ou Prometheus/Grafana
  4. **Logs centralisés** : Utilisez ELK Stack (Elasticsearch, Logstash, Kibana) pour centraliser les logs
  
  ## Conclusion
  
  Spring Boot et son écosystème offrent tous les outils nécessaires pour créer une architecture microservices robuste et évolutive. En suivant les bonnes pratiques et en utilisant les composants adaptés, vous pouvez construire des applications distribuées qui répondent aux exigences des systèmes modernes.
  
  Dans mon prochain article, nous approfondirons les stratégies de test pour les microservices.
        `,
        en: `
  # Microservices Architecture with Spring Boot
  
  Microservices architecture has become an essential approach for designing scalable and maintainable applications. In this article, we'll explore how to set up a solid microservices architecture with Spring Boot.
  
  ## Why Microservices?
  
  Microservices offer several advantages over traditional monolithic applications:
  
  - **Independent Deployment**: Each service can be deployed separately, facilitating continuous updates.
  - **Precise Scalability**: You can scale only the services that require more resources.
  - **Resilience**: Failure of one service doesn't cause the entire application to stop.
  - **Appropriate Technology**: Each service can use the technology most suitable for its function.
  
  ## Essential Components of a Microservices Architecture with Spring
  
  ### 1. Spring Boot for Services
  
  Spring Boot greatly simplifies the development of microservices thanks to its automatic configuration and starters. Here's an example of a minimal service:
  
  \`\`\`java
  @SpringBootApplication
  @RestController
  public class ProductServiceApplication {
  
      public static void main(String[] args) {
          SpringApplication.run(ProductServiceApplication.class, args);
      }
  
      @GetMapping("/products")
      public List<Product> getProducts() {
          // Logic to retrieve products
          return productRepository.findAll();
      }
  }
  \`\`\`
  
  ### 2. Spring Cloud Gateway for API Routing
  
  Spring Cloud Gateway serves as a unified entry point for your microservices architecture:
  
  \`\`\`java
  @SpringBootApplication
  public class GatewayApplication {
  
      public static void main(String[] args) {
          SpringApplication.run(GatewayApplication.class, args);
      }
  
      @Bean
      public RouteLocator customRouteLocator(RouteLocatorBuilder builder) {
          return builder.routes()
              .route("product_service", r -> r.path("/products/**")
                  .uri("lb://PRODUCT-SERVICE"))
              .route("order_service", r -> r.path("/orders/**")
                  .uri("lb://ORDER-SERVICE"))
              .build();
      }
  }
  \`\`\`
  
  ### 3. Service Discovery with Eureka
  
  To allow services to discover each other:
  
  \`\`\`java
  @SpringBootApplication
  @EnableEurekaServer
  public class DiscoveryServerApplication {
      public static void main(String[] args) {
          SpringApplication.run(DiscoveryServerApplication.class, args);
      }
  }
  \`\`\`
  
  ### 4. Centralized Configuration with Spring Cloud Config
  
  To manage the configuration of all your services in one central location:
  
  \`\`\`java
  @SpringBootApplication
  @EnableConfigServer
  public class ConfigServerApplication {
      public static void main(String[] args) {
          SpringApplication.run(ConfigServerApplication.class, args);
      }
  }
  \`\`\`
  
  ## Communication Between Services
  
  Communication between microservices can be done via:
  
  1. **REST** with RestTemplate or WebClient
  2. **Asynchronous Messaging** with Kafka or RabbitMQ
  3. **gRPC** for more efficient communication
  
  ## Securing Microservices
  
  Security is paramount in a distributed architecture. Use Spring Security with OAuth2/JWT to secure communications between services.
  
  ## Best Practices
  
  1. **Dedicated Database per Service**: Each microservice should have its own database
  2. **Circuit Breaker**: Use Resilience4j to manage service failures
  3. **Monitoring**: Set up a monitoring solution like Spring Boot Admin or Prometheus/Grafana
  4. **Centralized Logs**: Use ELK Stack (Elasticsearch, Logstash, Kibana) to centralize logs
  
  ## Conclusion
  
  Spring Boot and its ecosystem provide all the necessary tools to create a robust and scalable microservices architecture. By following best practices and using the right components, you can build distributed applications that meet the requirements of modern systems.
  
  In my next article, we'll explore testing strategies for microservices.
        `
      },
      author: "Igor Vannel",
      date: {
        fr: "15 Février 2025",
        en: "February 15, 2025"
      },
      category: {
        fr: "Architecture",
        en: "Architecture"
      },
      tags: ["Spring Boot", "Microservices", "Spring Cloud", "Java"],
      image: "/assets/images/blog/microservices.jpg"
    },
    {
      id: 2,
      slug: "react-native-best-practices",
      title: {
        fr: "Meilleures pratiques pour le développement React Native en 2025",
        en: "Best Practices for React Native Development in 2025"
      },
      excerpt: {
        fr: "Un guide complet des meilleures pratiques pour développer des applications React Native performantes et maintenables.",
        en: "A comprehensive guide to best practices for developing performant and maintainable React Native applications."
      },
      content: {
        fr: `
  # Meilleures pratiques pour le développement React Native en 2025
  
  React Native est devenu l'un des frameworks les plus populaires pour le développement d'applications mobiles cross-platform. Dans cet article, je partage les meilleures pratiques que j'ai acquises au cours de mes années d'expérience dans le développement d'applications React Native.
  
  ## Structure de projet
  
  Une bonne structure de projet est essentielle pour maintenir votre code organisé et faciliter la collaboration. Voici une structure recommandée :
  
  \`\`\`
  src/
    ├── api/                 # Services API et configuration
    ├── assets/              # Ressources statiques
    ├── components/          # Composants réutilisables
    │   ├── common/          # Composants génériques
    │   └── screens/         # Composants spécifiques aux écrans
    ├── navigation/          # Configuration de la navigation
    ├── screens/             # Écrans de l'application
    ├── store/               # État global (Redux, Zustand, etc.)
    ├── styles/              # Thèmes et styles partagés
    ├── utils/               # Fonctions utilitaires
    └── App.js               # Point d'entrée
  \`\`\`
  
  ## Gestion de l'état
  
  Avec React Native, plusieurs options s'offrent à vous pour gérer l'état de votre application :
  
  ### 1. Context API et hooks pour les états simples
  
  Pour les états simples, utilisez les hooks React et le Context API :
  
  \`\`\`jsx
  // ThemeContext.js
  import React, { createContext, useState, useContext } from 'react';
  
  const ThemeContext = createContext();
  
  export const ThemeProvider = ({ children }) => {
    const [darkMode, setDarkMode] = useState(false);
    
    return (
      <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
        {children}
      </ThemeContext.Provider>
    );
  };
  
  export const useTheme = () => useContext(ThemeContext);
  \`\`\`
  
  ### 2. Zustand pour une solution légère mais puissante
  
  Zustand est devenu une alternative populaire à Redux en 2025 :
  
  \`\`\`javascript
  import create from 'zustand';
  
  const useStore = create((set) => ({
    counter: 0,
    increment: () => set((state) => ({ counter: state.counter + 1 })),
    decrement: () => set((state) => ({ counter: state.counter - 1 })),
  }));
  
  // Dans vos composants
  function Counter() {
    const { counter, increment, decrement } = useStore();
    
    return (
      <View>
        <Text>{counter}</Text>
        <Button onPress={increment} title="+" />
        <Button onPress={decrement} title="-" />
      </View>
    );
  }
  \`\`\`
  
  ## Performance
  
  ### 1. Utiliser React.memo et useCallback
  
  Évitez les rendus inutiles avec React.memo et useCallback :
  
  \`\`\`jsx
  const ExpensiveComponent = React.memo(({ data, onPress }) => {
    // Rendu coûteux
    return <ComplexVisualization data={data} onPress={onPress} />;
  });
  
  function ParentComponent() {
    const [data, setData] = useState([]);
    
    // Mémorise la fonction pour éviter les recreations à chaque rendu
    const handlePress = useCallback(() => {
      // Logique
    }, []);
    
    return <ExpensiveComponent data={data} onPress={handlePress} />;
  }
  \`\`\`
  
  ### 2. Flatlist pour les longues listes
  
  Utilisez toujours FlatList au lieu de ScrollView pour les listes :
  
  \`\`\`jsx
  <FlatList
    data={items}
    renderItem={({ item }) => <ItemComponent item={item} />}
    keyExtractor={(item) => item.id.toString()}
    initialNumToRender={10}
    windowSize={5}
  />
  \`\`\`
  
  ### 3. Images optimisées
  
  Utilisez des images optimisées et FastImage pour une meilleure performance :
  
  \`\`\`jsx
  import FastImage from 'react-native-fast-image';
  
  <FastImage
    source={{ uri: imageUrl, priority: FastImage.priority.normal }}
    style={styles.image}
    resizeMode={FastImage.resizeMode.cover}
  />
  \`\`\`
  
  ## Navigation
  
  React Navigation est devenu le standard pour la navigation dans React Native. Voici comment structurer votre navigation :
  
  \`\`\`jsx
  import { NavigationContainer } from '@react-navigation/native';
  import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
  import { createStackNavigator } from '@react-navigation/stack';
  
  const Stack = createStackNavigator();
  const Tab = createBottomTabNavigator();
  
  function HomeStack() {
    return (
      <Stack.Navigator>
        <Stack.Screen name="Home" component={HomeScreen} />
        <Stack.Screen name="Details" component={DetailsScreen} />
      </Stack.Navigator>
    );
  }
  
  function AppNavigator() {
    return (
      <NavigationContainer>
        <Tab.Navigator>
          <Tab.Screen name="HomeTab" component={HomeStack} />
          <Tab.Screen name="Profile" component={ProfileScreen} />
          <Tab.Screen name="Settings" component={SettingsScreen} />
        </Tab.Navigator>
      </NavigationContainer>
    );
  }
  \`\`\`
  
  ## Tests
  
  ### 1. Tests unitaires avec Jest
  
  \`\`\`javascript
  import { render, fireEvent } from '@testing-library/react-native';
  import Button from '../Button';
  
  test('Button should call onPress when pressed', () => {
    const onPressMock = jest.fn();
    const { getByText } = render(<Button title="Press Me" onPress={onPressMock} />);
    
    fireEvent.press(getByText('Press Me'));
    
    expect(onPressMock).toHaveBeenCalledTimes(1);
  });
  \`\`\`
  
  ### 2. Tests E2E avec Detox
  
  Configurer et écrire des tests E2E avec Detox pour tester vos flux utilisateurs complets.
  
  ## Conclusion
  
  Le développement React Native continue d'évoluer rapidement. En suivant ces bonnes pratiques, vous pourrez créer des applications performantes, maintenables et de haute qualité qui répondent aux attentes des utilisateurs en 2025.
  
  Dans mon prochain article, nous explorerons les nouvelles fonctionnalités introduites dans React Native 0.74 et comment les exploiter dans vos applications.
        `,
        en: `
  # Best Practices for React Native Development in 2025
  
  React Native has become one of the most popular frameworks for cross-platform mobile app development. In this article, I share the best practices I've acquired over my years of experience in developing React Native applications.
  
  ## Project Structure
  
  A good project structure is essential for keeping your code organized and facilitating collaboration. Here's a recommended structure:
  
  \`\`\`
  src/
    ├── api/                 # API services and configuration
    ├── assets/              # Static resources
    ├── components/          # Reusable components
    │   ├── common/          # Generic components
    │   └── screens/         # Screen-specific components
    ├── navigation/          # Navigation configuration
    ├── screens/             # Application screens
    ├── store/               # Global state (Redux, Zustand, etc.)
    ├── styles/              # Shared themes and styles
    ├── utils/               # Utility functions
    └── App.js               # Entry point
  \`\`\`
  
  ## State Management
  
  With React Native, several options are available for managing your application's state:
  
  ### 1. Context API and hooks for simple states
  
  For simple states, use React hooks and the Context API:
  
  \`\`\`jsx
  // ThemeContext.js
  import React, { createContext, useState, useContext } from 'react';
  
  const ThemeContext = createContext();
  
  export const ThemeProvider = ({ children }) => {
    const [darkMode, setDarkMode] = useState(false);
    
    return (
      <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
        {children}
      </ThemeContext.Provider>
    );
  };
  
  export const useTheme = () => useContext(ThemeContext);
  \`\`\`
  
  ### 2. Zustand for a lightweight but powerful solution
  
  Zustand has become a popular alternative to Redux in 2025:
  
  \`\`\`javascript
  import create from 'zustand';
  
  const useStore = create((set) => ({
    counter: 0,
    increment: () => set((state) => ({ counter: state.counter + 1 })),
    decrement: () => set((state) => ({ counter: state.counter - 1 })),
  }));
  
  // In your components
  function Counter() {
    const { counter, increment, decrement } = useStore();
    
    return (
      <View>
        <Text>{counter}</Text>
        <Button onPress={increment} title="+" />
        <Button onPress={decrement} title="-" />
      </View>
    );
  }
  \`\`\`
  
  ## Performance
  
  ### 1. Using React.memo and useCallback
  
  Avoid unnecessary renders with React.memo and useCallback:
  
  \`\`\`jsx
  const ExpensiveComponent = React.memo(({ data, onPress }) => {
    // Expensive rendering
    return <ComplexVisualization data={data} onPress={onPress} />;
  });
  
  function ParentComponent() {
    const [data, setData] = useState([]);
    
    // Memoize the function to avoid recreations on each render
    const handlePress = useCallback(() => {
      // Logic
    }, []);
    
    return <ExpensiveComponent data={data} onPress={handlePress} />;
  }
  \`\`\`
  
  ### 2. FlatList for long lists
  
  Always use FlatList instead of ScrollView for lists:
  
  \`\`\`jsx
  <FlatList
    data={items}
    renderItem={({ item }) => <ItemComponent item={item} />}
    keyExtractor={(item) => item.id.toString()}
    initialNumToRender={10}
    windowSize={5}
  />
  \`\`\`
  
  ### 3. Optimized images
  
  Use optimized images and FastImage for better performance:
  
  \`\`\`jsx
  import FastImage from 'react-native-fast-image';
  
  <FastImage
    source={{ uri: imageUrl, priority: FastImage.priority.normal }}
    style={styles.image}
    resizeMode={FastImage.resizeMode.cover}
  />
  \`\`\`
  
  ## Navigation
  
  React Navigation has become the standard for navigation in React Native. Here's how to structure your navigation:
  
  \`\`\`jsx
  import { NavigationContainer } from '@react-navigation/native';
  import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
  import { createStackNavigator } from '@react-navigation/stack';
  
  const Stack = createStackNavigator();
  const Tab = createBottomTabNavigator();
  
  function HomeStack() {
    return (
      <Stack.Navigator>
        <Stack.Screen name="Home" component={HomeScreen} />
        <Stack.Screen name="Details" component={DetailsScreen} />
      </Stack.Navigator>
    );
  }
  
  function AppNavigator() {
    return (
      <NavigationContainer>
        <Tab.Navigator>
          <Tab.Screen name="HomeTab" component={HomeStack} />
          <Tab.Screen name="Profile" component={ProfileScreen} />
          <Tab.Screen name="Settings" component={SettingsScreen} />
        </Tab.Navigator>
      </NavigationContainer>
    );
  }
  \`\`\`
  
  ## Testing
  
  ### 1. Unit tests with Jest
  
  \`\`\`javascript
  import { render, fireEvent } from '@testing-library/react-native';
  import Button from '../Button';
  
  test('Button should call onPress when pressed', () => {
    const onPressMock = jest.fn();
    const { getByText } = render(<Button title="Press Me" onPress={onPressMock} />);
    
    fireEvent.press(getByText('Press Me'));
    
    expect(onPressMock).toHaveBeenCalledTimes(1);
  });
  \`\`\`
  
  ### 2. E2E tests with Detox
  
  Configure and write E2E tests with Detox to test your complete user flows.
  
  ## Conclusion
  
  React Native development continues to evolve rapidly. By following these best practices, you can create performant, maintainable, and high-quality applications that meet user expectations in 2025.
  
  In my next article, we'll explore the new features introduced in React Native 0.74 and how to leverage them in your applications.
        `
      },
      author: "Igor Vannel",
      date: {
        fr: "3 Mars 2025",
        en: "March 3, 2025"
      },
      category: {
        fr: "Développement Mobile",
        en: "Mobile Development"
      },
      tags: ["React Native", "JavaScript", "Mobile", "Performance"],
      image: "/assets/images/blog/react-native.jpg"
    },
    {
      id: 3,
      slug: "postgresql-performance-tips",
      title: {
        fr: "Optimisation des performances PostgreSQL pour les applications modernes",
        en: "PostgreSQL Performance Optimization for Modern Applications"
      },
      excerpt: {
        fr: "Découvrez des techniques avancées pour optimiser les performances de vos bases de données PostgreSQL dans des applications à forte charge.",
        en: "Learn advanced techniques to optimize the performance of your PostgreSQL databases in high-load applications."
      },
      content: {
        fr: `
  # Optimisation des performances PostgreSQL pour les applications modernes
  
  PostgreSQL est l'un des systèmes de gestion de base de données les plus puissants et flexibles, mais pour tirer le maximum de ses capacités, une optimisation appropriée est nécessaire. Dans cet article, je partage des techniques avancées que j'ai utilisées pour optimiser les performances de PostgreSQL dans des applications à forte charge.
  
  ## Indexation efficace
  
  ### Comprendre les types d'index
  
  PostgreSQL offre plusieurs types d'index adaptés à différentes situations :
  
  - **B-tree** : L'index par défaut, idéal pour les comparaisons d'égalité et de plage
  - **Hash** : Optimisé pour les comparaisons d'égalité uniquement
  - **GiST** : Pour les données géométriques et les recherches de texte intégral
  - **GIN** : Parfait pour les recherches dans les arrays ou les documents JSON
  - **BRIN** : Efficace pour de très grandes tables avec des données ordonnées naturellement
  
  ### Indexer intelligemment
  
  Créez des index composites pour les requêtes fréquentes :
  
  \`\`\`sql
  -- Pour une requête qui filtre par status et date
  CREATE INDEX idx_orders_status_date ON orders(status, created_at);
  \`\`\`
  
  Utilisez des index partiels pour les conditions fréquentes :
  
  \`\`\`sql
  -- Index uniquement les commandes actives
  CREATE INDEX idx_active_orders ON orders(id) WHERE status = 'active';
  \`\`\`
  
  ## Optimisation des requêtes
  
  ### Utiliser EXPLAIN ANALYZE
  
  Pour comprendre comment PostgreSQL exécute vos requêtes :
  
  \`\`\`sql
  EXPLAIN ANALYZE SELECT * FROM users WHERE email LIKE 'john%';
  \`\`\`
  
  ### Requêtes communes à optimiser
  
  #### Jointures N+1
  
  Problème courant dans les ORM :
  
  \`\`\`sql
  -- Au lieu de faire N requêtes comme celle-ci :
  SELECT * FROM comments WHERE post_id = 1;
  SELECT * FROM comments WHERE post_id = 2;
  ...
  
  -- Utilisez une seule requête avec IN :
  SELECT * FROM comments WHERE post_id IN (1, 2, 3, ...);
  \`\`\`
  
  #### Agrégations efficaces
  
  Utilisez des CTE (Common Table Expressions) pour les requêtes complexes :
  
  \`\`\`sql
  WITH monthly_sales AS (
    SELECT 
      date_trunc('month', order_date) AS month,
      SUM(amount) AS total
    FROM orders
    WHERE order_date >= '2024-01-01'
    GROUP BY date_trunc('month', order_date)
  )
  SELECT month, total, 
         total / LAG(total) OVER (ORDER BY month) - 1 AS growth_rate
  FROM monthly_sales
  ORDER BY month;
  \`\`\`
  
  ## Configuration du serveur
  
  ### Paramètres clés à ajuster
  
  Voici les paramètres les plus importants à considérer :
  
  \`\`\`
  # Mémoire
  shared_buffers = 25% of RAM (up to 8GB)
  work_mem = 32-64MB
  maintenance_work_mem = 256MB
  
  # Planificateur
  random_page_cost = 1.1 (si SSD)
  effective_cache_size = 75% of RAM
  
  # Checkpoints
  checkpoint_timeout = 15min
  max_wal_size = 2GB
  
  # Parallélisme
  max_worker_processes = cores
  max_parallel_workers_per_gather = cores / 2
  \`\`\`
  
  ## Partitionnement
  
  Pour les très grandes tables, utilisez le partitionnement :
  
  \`\`\`sql
  CREATE TABLE logs (
      id SERIAL,
      log_time TIMESTAMP,
      message TEXT
  ) PARTITION BY RANGE (log_time);
  
  CREATE TABLE logs_2024_q1 PARTITION OF logs
      FOR VALUES FROM ('2024-01-01') TO ('2024-04-01');
      
  CREATE TABLE logs_2024_q2 PARTITION OF logs
      FOR VALUES FROM ('2024-04-01') TO ('2024-07-01');
  \`\`\`
  
  ## Monitoring et maintenance
  
  ### Vacuum régulier
  
  Configurez l'autovacuum correctement et surveillez-le :
  
  \`\`\`sql
  -- Vérifier les tables qui nécessitent un vacuum
  SELECT relname, n_dead_tup, last_vacuum
  FROM pg_stat_user_tables
  ORDER BY n_dead_tup DESC;
  \`\`\`
  
  ### Outils de monitoring
  
  - **pg_stat_statements** pour identifier les requêtes lentes
  - **pgBadger** pour analyser les logs
  - **Prometheus + Grafana** pour surveiller en temps réel
  
  ## Conclusion
  
  L'optimisation de PostgreSQL est un processus continu qui nécessite une compréhension approfondie de votre charge de travail et des fonctionnalités de PostgreSQL. En suivant ces techniques, vous pouvez considérablement améliorer les performances de vos applications, même sous forte charge.
  
  Dans mon prochain article, nous explorerons comment configurer la réplication et la haute disponibilité avec PostgreSQL 16.
        `,
        en: `
  # PostgreSQL Performance Optimization for Modern Applications
  
  PostgreSQL is one of the most powerful and flexible database management systems, but to get the most out of its capabilities, proper optimization is necessary. In this article, I share advanced techniques I've used to optimize PostgreSQL performance in high-load applications.
  
  ## Efficient Indexing
  
  ### Understanding Index Types
  
  PostgreSQL offers several types of indexes suited to different situations:
  
  - **B-tree**: The default index, ideal for equality and range comparisons
  - **Hash**: Optimized for equality comparisons only
  - **GiST**: For geometric data and full-text searches
  - **GIN**: Perfect for searching in arrays or JSON documents
  - **BRIN**: Efficient for very large tables with naturally ordered data
  
  ### Indexing Intelligently
  
  Create composite indexes for frequent queries:
  
  \`\`\`sql
  -- For a query that filters by status and date
  CREATE INDEX idx_orders_status_date ON orders(status, created_at);
  \`\`\`
  
  Use partial indexes for frequent conditions:
  
  \`\`\`sql
  -- Index only active orders
  CREATE INDEX idx_active_orders ON orders(id) WHERE status = 'active';
  \`\`\`
  
  ## Query Optimization
  
  ### Using EXPLAIN ANALYZE
  
  To understand how PostgreSQL executes your queries:
  
  \`\`\`sql
  EXPLAIN ANALYZE SELECT * FROM users WHERE email LIKE 'john%';
  \`\`\`
  
  ### Common Queries to Optimize
  
  #### N+1 Joins
  
  Common problem in ORMs:
  
  \`\`\`sql
  -- Instead of making N queries like this:
  SELECT * FROM comments WHERE post_id = 1;
  SELECT * FROM comments WHERE post_id = 2;
  ...
  
  -- Use a single query with IN:
  SELECT * FROM comments WHERE post_id IN (1, 2, 3, ...);
  \`\`\`
  
  #### Efficient Aggregations
  
  Use CTEs (Common Table Expressions) for complex queries:
  
  \`\`\`sql
  WITH monthly_sales AS (
    SELECT 
      date_trunc('month', order_date) AS month,
      SUM(amount) AS total
    FROM orders
    WHERE order_date >= '2024-01-01'
    GROUP BY date_trunc('month', order_date)
  )
  SELECT month, total, 
         total / LAG(total) OVER (ORDER BY month) - 1 AS growth_rate
  FROM monthly_sales
  ORDER BY month;
  \`\`\`
  
  ## Server Configuration
  
  ### Key Parameters to Adjust
  
  Here are the most important parameters to consider:
  
  \`\`\`
  # Memory
  shared_buffers = 25% of RAM (up to 8GB)
  work_mem = 32-64MB
  maintenance_work_mem = 256MB
  
  # Planner
  random_page_cost = 1.1 (if SSD)
  effective_cache_size = 75% of RAM
  
  # Checkpoints
  checkpoint_timeout = 15min
  max_wal_size = 2GB
  
  # Parallelism
  max_worker_processes = cores
  max_parallel_workers_per_gather = cores / 2
  \`\`\`
  
  ## Partitioning
  
  For very large tables, use partitioning:
  
  \`\`\`sql
  CREATE TABLE logs (
      id SERIAL,
      log_time TIMESTAMP,
      message TEXT
  ) PARTITION BY RANGE (log_time);
  
  CREATE TABLE logs_2024_q1 PARTITION OF logs
      FOR VALUES FROM ('2024-01-01') TO ('2024-04-01');
      
  CREATE TABLE logs_2024_q2 PARTITION OF logs
      FOR VALUES FROM ('2024-04-01') TO ('2024-07-01');
  \`\`\`
  
  ## Monitoring and Maintenance
  
  ### Regular Vacuum
  
  Configure autovacuum correctly and monitor it:
  
  \`\`\`sql
  -- Check tables that need vacuuming
  SELECT relname, n_dead_tup, last_vacuum
  FROM pg_stat_user_tables
  ORDER BY n_dead_tup DESC;
  \`\`\`
  
  ### Monitoring Tools
  
  - **pg_stat_statements** to identify slow queries
  - **pgBadger** to analyze logs
  - **Prometheus + Grafana** for real-time monitoring
  
  ## Conclusion
  
  PostgreSQL optimization is an ongoing process that requires a deep understanding of your workload and PostgreSQL features. By following these techniques, you can significantly improve the performance of your applications, even under heavy load.
  
  In my next article, we'll explore how to set up replication and high availability with PostgreSQL 16.
        `
      },
      author: "Igor Vannel",
      date: {
        fr: "10 Avril 2025",
        en: "April 10, 2025"
      },
      category: {
        fr: "Bases de données",
        en: "Databases"
      },
      tags: ["PostgreSQL", "Performance", "Database", "Optimization"],
      image: "/assets/images/blog/postgresql.jpg"
    }
  ];